import React, { useEffect, useRef, useState } from 'react';
import './LandingPage.scss';
import logoIcon from './assets/landing/questa-logo-icon.png';
import iphoneMockup from './assets/landing/iphone-mockup.png';
import levelUpMockup from './assets/landing/level-up-mockup.png';
import aiPartnerMockup from './assets/landing/ai-partner-mockup.png';
import aiChatMockup from './assets/landing/ai-chat-mockup.png';
import aiReportMockup from './assets/landing/ai-report-fullscreen.png';
import calendarMockup from './assets/landing/calendar-mockup.png';
import questRewardMockup from './assets/landing/quest-reward-mockup.png';

const LandingPage = () => {
  const levelSectionRef = useRef(null);
  const aiPartnerSectionRef = useRef(null);
  const aiChatSectionRef = useRef(null);
  const aiReportSectionRef = useRef(null);
  const calendarSectionRef = useRef(null);
  const questRewardSectionRef = useRef(null);
  const handleAppStoreClick = () => {
    // iOS 앱 스토어 링크
    window.open('https://apps.apple.com/kr/app/%ED%80%98%EC%8A%A4%ED%83%80-%ED%95%98%EB%A3%A8%EB%A5%BC-%EA%B2%8C%EC%9E%84%EC%B2%98%EB%9F%BC-ai-%ED%8C%8C%ED%8A%B8%EB%84%88%EC%99%80-%ED%95%A8%EA%BB%98/id6748487900', '_blank');
  };

  const handlePlayStoreClick = () => {
    // Android Play Store 링크
    window.open('https://play.google.com/store/apps/details?id=com.ovs.androidquesta', '_blank');
  };

  const handleTryWebApp = () => {
    // 웹 버전 - 곧 출시 예정
    alert('웹 버전은 곧 출시 예정입니다! 조금만 기다려 주세요 😊');
  };

  // 스크롤 애니메이션 효과
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    if (levelSectionRef.current) {
      observer.observe(levelSectionRef.current);
    }

    if (aiPartnerSectionRef.current) {
      observer.observe(aiPartnerSectionRef.current);
    }

    if (aiChatSectionRef.current) {
      observer.observe(aiChatSectionRef.current);
    }

    if (aiReportSectionRef.current) {
      observer.observe(aiReportSectionRef.current);
    }

    if (calendarSectionRef.current) {
      observer.observe(calendarSectionRef.current);
    }

    if (questRewardSectionRef.current) {
      observer.observe(questRewardSectionRef.current);
    }

    return () => {
      if (levelSectionRef.current) {
        observer.unobserve(levelSectionRef.current);
      }
      if (aiPartnerSectionRef.current) {
        observer.unobserve(aiPartnerSectionRef.current);
      }
      if (aiChatSectionRef.current) {
        observer.unobserve(aiChatSectionRef.current);
      }
      if (aiReportSectionRef.current) {
        observer.unobserve(aiReportSectionRef.current);
      }
      if (calendarSectionRef.current) {
        observer.unobserve(calendarSectionRef.current);
      }
      if (questRewardSectionRef.current) {
        observer.unobserve(questRewardSectionRef.current);
      }
    };
  }, []);

  return (
    <div className="landing-page">
      {/* Header Section */}
      <header className="landing-header">
        <div className="logo-container">
          <div className="logo-text">QUESTA</div>
          <img src={logoIcon} alt="Questa Logo" className="logo-icon" />
        </div>
        <nav className="navigation">
          <button onClick={handleTryWebApp} className="nav-button">
            웹 버전 출시 예정
          </button>
        </nav>
      </header>

      {/* Main Content Section */}
      <main className="landing-main">
        <div className="content-container">
          <div className="text-content">
            <h1 className="main-headline">
              하루를 게임처럼, AI 파트너와 함께!
            </h1>
            <p className="sub-text">
              매일을 모험처럼, 성장을 게임처럼 즐겨보세요.<br />
              퀘스타와 함께라면 하루하루가 레벨업의 기회입니다.
            </p>
            <div className="cta-buttons">
              <button onClick={handleAppStoreClick} className="cta-button primary">
                <span className="button-icon">📱</span>
                iOS - App Store에서 다운로드
              </button>
              <button onClick={handlePlayStoreClick} className="cta-button primary">
                <span className="button-icon">🤖</span>
                Android - Play Store에서 다운로드
              </button>
            </div>
          </div>

          <div className="mockup-container">
            <img
              src={iphoneMockup}
              alt="Questa App Screenshot"
              className="iphone-mockup"
            />
          </div>
        </div>
      </main>

      {/* Second Section - Level Up */}
      <section className="level-section" ref={levelSectionRef}>
        <div className="content-container">
          <div className="text-content animate-text">
            <h2 className="section-headline">
              퀘스트를 클리어하다 보면 레벨업!
            </h2>
            <p className="section-text">
              완료한 일정을 눈으로 확인하고, 퀘스트 보상으로 더 많은 경험치를 쌓아보세요.<br />
              성장의 재미를 느낄 수 있어요.
            </p>
          </div>

          <div className="mockup-container animate-image">
            <img
              src={levelUpMockup}
              alt="Questa Level Up Feature"
              className="iphone-mockup level-mockup"
            />
          </div>
        </div>
      </section>

      {/* Third Section - AI Report */}
      <section className="ai-report-section" ref={aiReportSectionRef}>
        <div className="content-container">
          <div className="mockup-container animate-image">
            <img
              src={aiReportMockup}
              alt="Questa AI Report Feature"
              className="iphone-mockup ai-report-mockup"
            />
          </div>

          <div className="text-content animate-text">
            <h2 className="section-headline">
              AI가 분석한 나만의 성장 리포트
            </h2>
            <p className="section-text">
              AI 파트너가 나의 활동을 분석하고, 주간 성과 리포트를 제공해드려요.<br />
              데이터 기반의 인사이트로 더 나은 내일을 준비하세요.
            </p>
          </div>
        </div>
      </section>

      {/* Fourth Section - AI Partner */}
      <section className="ai-partner-section" ref={aiPartnerSectionRef}>
        <div className="content-container">
          <div className="mockup-container animate-image">
            <img
              src={aiPartnerMockup}
              alt="Questa AI Partner Feature"
              className="iphone-mockup ai-partner-mockup"
            />
          </div>

          <div className="text-content animate-text">
            <h2 className="section-headline">
              나만의 AI 파트너와 함께!
            </h2>
            <p className="section-text">
              개인 맞춤형 AI가 당신의 일정을 분석하고, 최적의 계획을 제안해드려요.<br />
              더 스마트하게 하루를 관리해보세요.
            </p>
          </div>
        </div>
      </section>

      {/* Fifth Section - AI Chat */}
      <section className="ai-chat-section" ref={aiChatSectionRef}>
        <div className="content-container">
          <div className="text-content animate-text">
            <h2 className="section-headline">
              나만의 AI 파트너와 함께!
            </h2>
            <p className="section-text">
              AI 파트너가 일정을 등록하고 수정해줄 뿐만 아니라, 계획과 목표까지 제시해줘요.<br />
              나만의 파트너와 함께 성장하세요!
            </p>
          </div>

          <div className="mockup-container animate-image">
            <img
              src={aiChatMockup}
              alt="Questa AI Chat Feature"
              className="iphone-mockup ai-chat-mockup"
            />
          </div>
        </div>
      </section>

      {/* Sixth Section - Calendar */}
      <section className="calendar-section" ref={calendarSectionRef}>
        <div className="content-container">
          <div className="mockup-container animate-image">
            <img
              src={calendarMockup}
              alt="Questa Calendar Feature"
              className="iphone-mockup calendar-mockup"
            />
          </div>

          <div className="text-content animate-text">
            <h2 className="section-headline calendar-headline">
              한눈에 보기 쉬운 나만의 캘린더
            </h2>
            <p className="section-text">
              일정을 달력에 직관적으로 표시해드려요. 나의 스케쥴을 쉽게 파악할 수 있어요.
            </p>
          </div>
        </div>
      </section>

      {/* Seventh Section - Quest Reward */}
      <section className="quest-reward-section" ref={questRewardSectionRef}>
        <div className="content-container">
          <div className="text-content animate-text">
            <h2 className="section-headline">
              퀘스트 클리어하고 보상 획득!
            </h2>
            <p className="section-text">
              퀘스트를 완료할 때마다 포인트가 쌓이고, 다양한 리워드로 교환할 수 있어요.<br />
              모험처럼 즐기는 성취감을 느껴보세요.
            </p>
          </div>

          <div className="mockup-container animate-image">
            <img
              src={questRewardMockup}
              alt="Questa Quest Reward Feature"
              className="iphone-mockup quest-reward-mockup"
            />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta-section">
        <div className="cta-content">
          <h2 className="cta-headline">지금 시작하고 성장하세요!</h2>
          <p className="cta-text">
            퀘스타와 함께 매일매일 성장하는 나를 경험하세요
          </p>
          <div className="cta-buttons-container">
            <button onClick={handleAppStoreClick} className="cta-download-button primary">
              <span className="button-icon">📱</span>
              <div className="button-text">
                <span className="button-label">Download on the</span>
                <span className="button-store">App Store</span>
              </div>
            </button>
            <button onClick={handlePlayStoreClick} className="cta-download-button primary">
              <span className="button-icon">🤖</span>
              <div className="button-text">
                <span className="button-label">Download on the</span>
                <span className="button-store">Google Play</span>
              </div>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <p>&copy; 2024 Questa Life. All rights reserved.</p>
          <div className="footer-links">
            <a href="/privacy" target="_blank" rel="noopener noreferrer">
              개인정보처리방침
            </a>
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              이용약관
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;